import { isoAttr } from "../../lib/datetime.ts";
import { useId, useState, type ReactNode } from "react";
import { DenseTable, StatusBadge, useDialogFocus, type Column } from "../../components/index.ts";
import type { RoleState } from "../../state/identity.tsx";
import { WriteButton, canWrite } from "./WriteControls.tsx";
import { useAcknowledgeAlert } from "./use-alerts.ts";
import {
  alertLevel,
  alertStateLabel,
  formatTime,
  formatValue,
  severityLabel,
  validateAckReason,
  writeErrorMessage,
  type Alert,
} from "./vocab.ts";

/**
 * The firing + history list for the Alerts screen (task 11.13). It renders a semantic
 * {@link DenseTable} of alerts (design 12.4) and, per row, an ACK affordance:
 *
 * - FIRING alerts are prominent — the status is shown by icon+text (never colour alone, design
 *   12.4), and a firing/pending alert is never painted green (design 12.3).
 * - Each alert is ackable via `POST /alerts/{id}/ack` (operator). The ack control is HIDDEN-OR-
 *   DISABLED-WITH-A-REASON for a viewer / unknown role ({@link WriteButton}); the server enforces
 *   regardless and a 403 is surfaced in the dialog.
 * - Acknowledging requires a reason (mandatory; FR-10.5): the dialog blocks submit without one and
 *   also surfaces the server's 400/422 if the server still rejects (defence in depth).
 */
export function AlertList(props: {
  readonly alerts: readonly Alert[];
  readonly role: RoleState;
  readonly caption: string;
  readonly emptyMessage: string;
}): ReactNode {
  const [ackingId, setAckingId] = useState<string | null>(null);

  const columns: readonly Column<Alert>[] = [
    {
      key: "state",
      header: "State",
      width: "sm",
      cell: (a) => (
        <StatusBadge
          level={alertLevel(a)}
          label={alertStateLabel(a.state)}
          title={`${alertStateLabel(a.state)} · ${severityLabel(a.severity)}`}
          size="sm"
        />
      ),
    },
    {
      key: "rule",
      header: "Rule",
      isRowHeader: true,
      cell: (a) => (
        <span className="alr-rule">
          <span className="alr-rule__name">{a.rule_name}</span>
          <span className="alr-rule__labels alr-faint">{labelLine(a.labels)}</span>
        </span>
      ),
    },
    {
      key: "severity",
      header: "Severity",
      width: "sm",
      cell: (a) => severityLabel(a.severity),
    },
    {
      key: "value",
      header: "Value",
      align: "right",
      width: "sm",
      cell: (a) => <span className="alr-num">{formatValue(a.value)}</span>,
    },
    {
      key: "since",
      header: "Since",
      cell: (a) => <time dateTime={isoAttr(a.since)}>{formatTime(a.since)}</time>,
    },
    {
      key: "ack",
      header: "Acknowledgement",
      cell: (a) => <AckCell alert={a} role={props.role} onAck={() => setAckingId(a.id)} />,
    },
  ];

  const acking = props.alerts.find((a) => a.id === ackingId) ?? null;

  return (
    <>
      <DenseTable
        caption={props.caption}
        columns={columns}
        rows={props.alerts}
        rowKey={(a) => a.id}
        emptyMessage={props.emptyMessage}
        stickyHeader
      />
      {acking !== null && (
        <AckDialog
          alert={acking}
          role={props.role}
          onClose={() => setAckingId(null)}
        />
      )}
    </>
  );
}

/** The per-row ack cell: the current ack overlay if present, else an ack button (role-gated). */
function AckCell(props: { alert: Alert; role: RoleState; onAck: () => void }): ReactNode {
  const ack = props.alert.acknowledged;
  if (ack !== null) {
    return (
      <span className="alr-ack">
        <StatusBadge level="neutral" label="Acknowledged" size="sm" title={`Acknowledged by ${ack.by}`} />
        <span className="alr-faint alr-ack__reason">“{ack.reason}”</span>
      </span>
    );
  }
  // Resolved alerts cannot be acknowledged; only live ones get the control.
  if (props.alert.state === "resolved") return <span className="alr-faint">n/a</span>;
  return (
    <WriteButton role={props.role} onClick={props.onAck} variant="primary">
      Acknowledge
    </WriteButton>
  );
}

/**
 * The acknowledgement dialog. Reason is MANDATORY: submit is blocked until a non-empty reason is
 * entered ({@link validateAckReason}), and the server's 400/422 is still surfaced if the server
 * rejects. An optional bounded `expires_in_s` is offered (the contract bound: 60 s … 7 days);
 * without it the ack ends when the alert resolves.
 */
function AckDialog(props: { alert: Alert; role: RoleState; onClose: () => void }): ReactNode {
  const titleId = useId();
  const reasonId = useId();
  const expiryId = useId();
  const [reason, setReason] = useState("");
  const [expiryHours, setExpiryHours] = useState("");
  const mutation = useAcknowledgeAlert();

  const reasonError = validateAckReason(reason);
  const expiryS = parseExpiryHours(expiryHours);
  const expiryError =
    expiryHours.trim().length > 0 && expiryS === null
      ? "Enter a duration between 1 minute and 7 days, or leave blank."
      : null;
  const canSubmit = canWrite(props.role) && reasonError === null && expiryError === null && !mutation.isPending;

  const submit = (): void => {
    if (!canSubmit) return;
    mutation.mutate(
      {
        id: props.alert.id,
        body: {
          reason: reason.trim(),
          ...(expiryS === null ? {} : { expires_in_s: expiryS }),
        },
      },
      { onSuccess: () => props.onClose() },
    );
  };

  // Trap/restore focus and wire Escape-to-close for the modal (design 12.4).
  const dialogRef = useDialogFocus<HTMLDivElement>(props.onClose);

  return (
    <div className="alr-dialog-backdrop" role="presentation" onClick={props.onClose}>
      <div
        ref={dialogRef}
        className="alr-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titleId} className="alr-dialog__title">
          Acknowledge “{props.alert.rule_name}”
        </h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label className="alr-field">
            <span className="alr-field__label">Reason (required)</span>
            <textarea
              id={reasonId}
              className="alr-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={256}
              required
              aria-invalid={reason.length > 0 && reasonError !== null}
              placeholder="Why are you acknowledging this?"
            />
            {reason.length > 0 && reasonError !== null && (
              <span className="alr-field__error" role="alert">
                {reasonError}
              </span>
            )}
          </label>

          <label className="alr-field">
            <span className="alr-field__label">Expires after (hours, optional)</span>
            <input
              id={expiryId}
              className="alr-input"
              type="number"
              min={0}
              step="0.5"
              value={expiryHours}
              onChange={(e) => setExpiryHours(e.target.value)}
              placeholder="Leave blank to end when the alert resolves"
              aria-invalid={expiryError !== null}
            />
            {expiryError !== null && (
              <span className="alr-field__error" role="alert">
                {expiryError}
              </span>
            )}
          </label>

          {mutation.isError && (
            <p className="alr-field__error" role="alert">
              {writeErrorMessage(mutation.error, "acknowledgement")}
            </p>
          )}

          <div className="alr-dialog__actions">
            <WriteButton role={props.role} type="submit" variant="primary" disabled={!canSubmit} onClick={submit}>
              {mutation.isPending ? "Acknowledging…" : "Acknowledge"}
            </WriteButton>
            <button type="button" className="alr-btn" data-variant="ghost" onClick={props.onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Parse an "hours" string into seconds within the contract bound (60 s … 604800 s), or null. */
function parseExpiryHours(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;
  const hours = Number(trimmed);
  if (!Number.isFinite(hours) || hours <= 0) return null;
  const seconds = Math.round(hours * 3600);
  if (seconds < 60 || seconds > 604_800) return null;
  return seconds;
}

/** A compact label line for an alert's labels. */
function labelLine(labels: Readonly<Record<string, string>>): string {
  const entries = Object.entries(labels);
  if (entries.length === 0) return "";
  return entries.map(([k, v]) => `${k}=${v}`).join(", ");
}
